import React, { useState } from 'react'
import { Smartphone, Wallet, Download } from 'lucide-react'
import {
  isAppleWalletAvailable,
  isGoogleWalletAvailable,
  addToAppleWallet,
  addToGoogleWallet,
  WalletPassData
} from '../../lib/wallet'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface AddToWalletButtonProps {
  passData: WalletPassData
  className?: string
}

const AddToWalletButton: React.FC<AddToWalletButtonProps> = ({ passData, className = '' }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [walletType, setWalletType] = useState<'apple' | 'google' | null>(null)

  const appleWalletAvailable = isAppleWalletAvailable()
  const googleWalletAvailable = isGoogleWalletAvailable()
  const anyWalletAvailable = appleWalletAvailable || googleWalletAvailable

  const handleAddToWallet = async (type: 'apple' | 'google') => {
    try {
      setIsLoading(true)
      setWalletType(type)

      if (type === 'apple') {
        await addToAppleWallet(passData)
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            title="Opening Wallet"
            message="Opening Apple Wallet"
            type="success"
            visible={t.visible}
          />
        ))
      } else {
        await addToGoogleWallet(passData)
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            title="Opening Wallet"
            message="Opening Google Wallet"
            type="success"
            visible={t.visible}
          />
        ))
      }
    } catch (error) {
      console.error('Failed to add to wallet:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Wallet Failed"
          message={`Could not add to ${type === 'apple' ? 'Apple' : 'Google'} Wallet`}
          type="error"
          visible={t.visible}
        />
      ))
    } finally {
      setIsLoading(false)
      setWalletType(null)
    }
  }

  if (!anyWalletAvailable) {
    return (
      <div className={`bg-secret-bg-secondary rounded-lg p-4 border border-secret-border ${className}`}>
        <div className="flex items-start space-x-3">
          <Wallet className="text-secret-text-tertiary flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="text-sm font-medium text-white mb-1">Digital Wallet Pass</h4>
            <p className="text-xs text-secret-text-secondary">
              Add this order to Apple Wallet or Google Wallet on your mobile device for quick access.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="text-center mb-2">
        <h4 className="text-sm font-medium text-white mb-1">Add to Digital Wallet</h4>
        <p className="text-xs text-secret-text-secondary">
          Access your order offline with Apple Wallet or Google Wallet
        </p>
      </div>

      {appleWalletAvailable && (
        <button
          onClick={() => handleAddToWallet('apple')}
          disabled={isLoading}
          className="w-full bg-black hover:bg-gray-900 text-white font-medium rounded-lg px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          style={{
            border: '1px solid #000',
          }}
        >
          {isLoading && walletType === 'apple' ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span>Add to Apple Wallet</span>
            </>
          )}
        </button>
      )}

      {googleWalletAvailable && (
        <button
          onClick={() => handleAddToWallet('google')}
          disabled={isLoading}
          className="w-full bg-white hover:bg-gray-100 text-black font-medium rounded-lg px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          style={{
            border: '1px solid #dadce0',
          }}
        >
          {isLoading && walletType === 'google' ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#FFC107"/>
                <path d="M3.15283 7.3455L6.43833 9.755C7.32733 7.554 9.48033 6 11.9998 6C13.5293 6 14.9208 6.577 15.9803 7.5195L18.8088 4.691C17.0228 3.0265 14.6338 2 11.9998 2C8.15883 2 4.82783 4.1685 3.15283 7.3455Z" fill="#FF3D00"/>
                <path d="M12.0002 22C14.5832 22 16.9302 21.0115 18.7047 19.404L15.6097 16.785C14.5719 17.5742 13.3039 18.001 12.0002 18C9.39916 18 7.19066 16.3415 6.35866 14.027L3.09766 16.5395C4.75266 19.778 8.11366 22 12.0002 22Z" fill="#4CAF50"/>
                <path d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.7845L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#1976D2"/>
              </svg>
              <span>Add to Google Wallet</span>
            </>
          )}
        </button>
      )}

      <div className="flex items-center justify-center space-x-2 text-xs text-secret-text-tertiary pt-2">
        <Download size={12} />
        <span>Pass includes QR code and order details</span>
      </div>
    </div>
  )
}

export default AddToWalletButton
